import { validateSPBTableInterface } from "../helpers/validateSPBTableInterface";
import Tips from "./tips";

/**
 * Загружает HTML partial по указанному пути и вставляет его в контейнер по селектору.
 *
 * @async
 * @param {string} selector - CSS-селектор контейнера, куда будет вставлен partial.
 * @param {string} partialPath - Путь к HTML partial-файлу (относительно public).
 * @returns {Promise<void>}
 */
export async function loadPartial(selector, partialPath) {
    const container = document.querySelector(selector);

    if (!container) {
        console.warn(`Container not found: ${selector}`);
        return;
    }

    const response = await fetch(partialPath);
    if (!response.ok) {
        container.innerHTML = "Ошибка загрузки блока";
        throw new Error(`Не удалось загрузить partial: ${partialPath} (статус ${response.status})`);
    }

    const html = await response.text();
    container.innerHTML = html;
}

/**
 * Открывает боковое меню, загружает partial и инициализирует компоненты или слушатели в зависимости от категории.
 *
 * @async
 * @param {string} selector - Название блока или компонента (например, "table_base" или "main_btns").
 * @param {string} [menuTitle] - Заголовок меню (опционально).
 * @returns {Promise<void>}
 */
export async function openCorrectSideMenu(selector, menuTitle) {
    const listenersModules = import.meta.glob("../partials/**/[a-zA-Z0-9_]*.js");

    /** @type {CategoryConfig|null} */
    let category = null;

    for (const [key, config] of Object.entries(CATEGORIES)) {
        if (config.match(selector)) {
            category = { name: key, ...config };
            break;
        }
    }

    const isDisableShowTipsBtn = !(category || selector === "main_btns");
    const showTipsBtn = document.getElementById("show-tips");

    showTipsBtn.classList.toggle("disable", isDisableShowTipsBtn);

    if (category) {
        // Категориальный partial
        const partialPath = category.htmlPath(selector);
        await loadPartial("#side_menu_container", partialPath);

        // Компоненты
        const componentMaps = import.meta.glob("../partials/**/componentMap.js");
        const componentMapModule = await componentMaps[category.componentMapPath(selector)]();
        if (componentMapModule.componentMap) {
            await loadAllComponents(componentMapModule.componentMap);
        }

        // Инициализация общих слушателей (например, table_config.js или stoyki_config.js)
        if (listenersModules[category.configPath]) {
            try {
                const listenersModule = await listenersModules[category.configPath]();
                const initFn = listenersModule?.[category.initMethod];
                if (typeof initFn === "function") {
                    initFn();
                }
            } catch (e) {
                console.warn(`Ошибка при инициализации слушателей для категории "${category.name}":`, e);
            }
        } else {
            console.warn(`Нет файла слушателей для категории "${category.name}":`, category.configPath);
        }

        const tipSelectSize = document.createElement("div");
        tipSelectSize.classList.add("tip-select-size");

        const menuBody = document.querySelector("#form-object-params");
        menuBody.prepend(tipSelectSize);

        Tips.showOnce("product");
    } else {
        // Обычный partial (не из категорий, без компонентов)
        const partialPath = `./partials/${selector}/index.html`;
        await loadPartial("#side_menu_container", partialPath);

        const pathImport = `../partials/${selector}/${selector}.js`;
        if (listenersModules[pathImport]) {
            try {
                const listenersModule = await listenersModules[pathImport]();
                if (typeof listenersModule?.initListeners === "function") {
                    listenersModule.initListeners();
                }
            } catch (e) {
                console.error(`Ошибка при инициализации слушателей для ${selector}:`, e);
            }
        } else {
            console.error(`Нет файла слушателей для ${selector}:`, pathImport);
        }
    }

    // Установка заголовка меню
    if (menuTitle) {
        const menuTitleEl = document.querySelector("#side_menu_container .menu_title");
        if (menuTitleEl) menuTitleEl.innerHTML = menuTitle;
    }

    // Специфичная валидация для СПБ-столов
    if (selector.includes("spb")) {
        validateSPBTableInterface();
    }
}

/**
 * Загружает и инициализирует компоненты, указанные в объекте componentMap.
 *
 * @async
 * @param {Object.<string, function>} componentMap - Объект с ключами (ID DOM-элементов) и функциями-инициализаторами.
 * @returns {Promise<void>}
 */
export async function loadAllComponents(componentMap) {
    const containers = Array.from(document.querySelectorAll("[data-component][id]"));
    await Promise.all(
        containers.map(async (container) => {
            const id = container.id;
            if (componentMap && componentMap[id]) {
                return componentMap[id](container);
            }
        }),
    );
}

/**
 * Конфигурация всех категорий, поддерживающих компоненты и отдельные конфиг-файлы.
 *
 * Чтобы добавить новую категорию:
 * 1. Укажи папку, где будут лежать HTML-файлы и componentMap.js.
 * 2. Укажи общий JS-файл категории (например, table_config.js).
 * 3. Укажи, как определить, что selector принадлежит этой категории (в match).
 *
 * @typedef {Object} CategoryConfig
 * @property {(selector: string) => boolean} match - Функция, проверяющая, принадлежит ли selector данной категории.
 * @property {(selector: string) => string} htmlPath - Функция, возвращающая путь к HTML partial.
 * @property {(selector: string) => string} componentMapPath - Функция, возвращающая путь к componentMap.js.
 * @property {string} configPath - Путь к конфигурационному JS-файлу категории.
 * @property {string} initMethod - Название функции инициализации в этом файле.
 */

const CATEGORIES = {
    /**
     * Категория столов (tables)
     */
    tables: {
        match: (selector) =>
            import.meta.glob("../partials/table_config/tables/**/componentMap.js")[
                `../partials/table_config/tables/${selector}/componentMap.js`
            ],
        htmlPath: (selector) => `./partials/table_config/tables/${selector}/${selector}.html`,
        componentMapPath: (selector) => `../partials/table_config/tables/${selector}/componentMap.js`,
        configPath: "../partials/table_config/table_config.js",
        initMethod: "initListeners",
    },

    /**
     * Категория подкатных стоек (stoyki_podkatnie)
     */
    stoyki_podkatnie: {
        match: (selector) =>
            import.meta.glob("../partials/table_config/stoyki_podkatnie/**/componentMap.js")[
                `../partials/table_config/stoyki_podkatnie/${selector}/componentMap.js`
            ],
        htmlPath: (selector) => `./partials/table_config/stoyki_podkatnie/${selector}/${selector}.html`,
        componentMapPath: (selector) => `../partials/table_config/stoyki_podkatnie/${selector}/componentMap.js`,
        configPath: "../partials/table_config/table_config.js",
        initMethod: "initListeners",
    },

    /**
     * Категория подкатных столов (stoli_podkatnie)
     */
    stoli_podkatnie: {
        match: (selector) =>
            import.meta.glob("../partials/table_config/stoli_podkatnie/**/componentMap.js")[
                `../partials/table_config/stoli_podkatnie/${selector}/componentMap.js`
            ],
        htmlPath: (selector) => `./partials/table_config/stoli_podkatnie/${selector}/${selector}.html`,
        componentMapPath: (selector) => `../partials/table_config/stoli_podkatnie/${selector}/componentMap.js`,
        configPath: "../partials/table_config/table_config.js",
        initMethod: "initListeners",
    },

    telezhki: {
        match: (selector) =>
            import.meta.glob("../partials/table_config/telezhki/**/componentMap.js")[
                `../partials/table_config/telezhki/${selector}/componentMap.js`
            ],
        htmlPath: (selector) => `./partials/table_config/telezhki/${selector}/${selector}.html`,
        componentMapPath: (selector) => `../partials/table_config/telezhki/${selector}/componentMap.js`,
        configPath: "../partials/table_config/table_config.js",
        initMethod: "initListeners",
    },

    tumbi: {
        match: (selector) =>
            import.meta.glob("../partials/table_config/tumbi/**/componentMap.js")[
                `../partials/table_config/tumbi/${selector}/componentMap.js`
            ],
        htmlPath: (selector) => `./partials/table_config/tumbi/${selector}/${selector}.html`,
        componentMapPath: (selector) => `../partials/table_config/tumbi/${selector}/componentMap.js`,
        configPath: "../partials/table_config/table_config.js",
        initMethod: "initListeners",
    },

    stellazhi: {
        match: (selector) =>
            import.meta.glob("../partials/table_config/stellazhi/**/componentMap.js")[
                `../partials/table_config/stellazhi/${selector}/componentMap.js`
            ],
        htmlPath: (selector) => `./partials/table_config/stellazhi/${selector}/${selector}.html`,
        componentMapPath: (selector) => `../partials/table_config/stellazhi/${selector}/componentMap.js`,
        configPath: "../partials/table_config/table_config.js",
        initMethod: "initListeners",
    },

    stulya: {
        match: (selector) =>
            import.meta.glob("../partials/table_config/stulya/**/componentMap.js")[
                `../partials/table_config/stulya/${selector}/componentMap.js`
            ],
        htmlPath: (selector) => `./partials/table_config/stulya/${selector}/${selector}.html`,
        componentMapPath: (selector) => `../partials/table_config/stulya/${selector}/componentMap.js`,
        configPath: "../partials/table_config/table_config.js",
        initMethod: "initListeners",
    },

    verstaki: {
        match: (selector) =>
            import.meta.glob("../partials/table_config/verstaki/**/componentMap.js")[
                `../partials/table_config/verstaki/${selector}/componentMap.js`
            ],
        htmlPath: (selector) => `./partials/table_config/verstaki/${selector}/${selector}.html`,
        componentMapPath: (selector) => `../partials/table_config/verstaki/${selector}/componentMap.js`,
        configPath: "../partials/table_config/table_config.js",
        initMethod: "initListeners",
    },

    wardrobes: {
        match: (selector) =>
            import.meta.glob("../partials/table_config/wardrobes/**/componentMap.js")[
                `../partials/table_config/wardrobes/${selector}/componentMap.js`
            ],
        htmlPath: (selector) => `./partials/table_config/wardrobes/${selector}/${selector}.html`,
        componentMapPath: (selector) => `../partials/table_config/wardrobes/${selector}/componentMap.js`,
        configPath: "../partials/table_config/table_config.js",
        initMethod: "initListeners",
    },

    // Пример добавления новой категории:
    // my_category: {
    //     match: (selector) => import.meta.glob("../partials/my_category/**/componentMap.js")[`../partials/my_category/${selector}/componentMap.js`],
    //     htmlPath: (selector) => `./partials/my_category/${selector}/${selector}.html`,
    //     componentMapPath: (selector) => `../partials/my_category/${selector}/componentMap.js`,
    //     configPath: "../partials/my_category/my_category_config.js",
    //     initMethod: "initListeners",
    // }
};
